import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FileText, TrendingUp, FileSignature, Calendar, ClipboardList, Navigation2 } from 'lucide-react';
import { agendaApi, ordersApi } from '../api';

/**
 * Mobile bottom navigation — matches Google Calendar-like tab bar style.
 * Visible only on <lg screens. Shows badges for pending items.
 */
export default function MobileBottomNav() {
  const location = useLocation();
  const [agendaCount, setAgendaCount] = useState(0);
  const [osCount, setOsCount] = useState(0);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const list = await agendaApi.list();
        if (!mounted) return;
        const now = new Date();
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        const upcoming = (list || []).filter(ev => {
          const d = new Date(ev.start_at);
          return d >= now && d <= monthEnd;
        }).length;
        setAgendaCount(upcoming);
      } catch { setAgendaCount(0); }
      try {
        const orders = await ordersApi.list({ status: 'scheduled' });
        if (!mounted) return;
        setOsCount((orders || []).length);
      } catch { setOsCount(0); }
    })();
    return () => { mounted = false; };
  }, [location.pathname]);

  const items = [
    { to: '/', label: 'Dashboard', icon: FileText, badge: null, exact: true },
    { to: '/funil', label: 'Funil', icon: TrendingUp, badge: 'WA', badgeColor: 'bg-emerald-500' },
    { to: '/contratos', label: 'Contratos', icon: FileSignature, badge: null },
    { to: '/agenda', label: 'Agenda', icon: Calendar, badge: agendaCount > 0 ? agendaCount : null, badgeColor: 'bg-slate-800' },
    { to: '/ordens', label: 'O.S.', icon: ClipboardList, badge: osCount > 0 ? osCount : null, badgeColor: 'bg-slate-800' },
    { to: '/rotas', label: 'Rotas', icon: Navigation2, badge: null },
  ];

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-slate-200 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]"
      data-testid="mobile-bottom-nav"
    >
      <div className="grid grid-cols-6 h-16">
        {items.map(it => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.exact}
            data-testid={`bottom-nav-${it.label.toLowerCase().replace(/\W/g, '')}`}
            className={({ isActive }) => `flex flex-col items-center justify-center relative gap-0.5 px-1 pt-1 pb-1 text-[10px] font-semibold transition-colors ${
              isActive ? 'text-emerald-600' : 'text-slate-500'
            }`}
          >
            {({ isActive }) => (
              <>
                <div className="relative">
                  <it.icon className="w-5 h-5" />
                  {it.badge != null && it.badge !== '' && (
                    <span className={`absolute -top-2 -right-2.5 min-w-[18px] h-[18px] px-1 rounded-full text-white text-[9px] font-bold flex items-center justify-center ${it.badgeColor || 'bg-emerald-500'}`}>
                      {it.badge}
                    </span>
                  )}
                </div>
                <span className="leading-none">{it.label}</span>
                {isActive && <span className="absolute -bottom-0.5 w-1 h-1 rounded-full bg-emerald-600" />}
              </>
            )}
          </NavLink>
        ))}
      </div>
      {/* Safe area padding for iOS */}
      <div className="h-safe-bottom bg-white" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} />
    </nav>
  );
}
